import type { UpdateOfflineRecordingSettingsArgs } from '@/tauri/offline-recording/recording';
import { logger } from '@/utils/logger/logger';
import { GenerationMethod } from '@heidi/schema/ml-scribe';
import { DEFAULT_TEMPLATE_AUTO_MODE } from '@heidi/session/contracts';
import type { OfflineConsultNoteSettings } from '@heidi/session/data';

// The marker + read predicate moved to `@heidi/session/contracts` with the
// D3 family — session owns its cache-entry contract; this module keeps the
// STAMPER (offline-recording writes the marker, session only reads it).
import {
  isOfflineLocalSession,
  OFFLINE_LOCAL_SESSION_MARKER,
  type OfflineLocalSessionMarker,
  type QuerySessionResponse,
} from '@heidi/session/contracts';

export { isOfflineLocalSession, type OfflineLocalSessionMarker };

type SessionWithOfflineMarker = QuerySessionResponse & OfflineLocalSessionMarker;

export function markOfflineLocalSession(
  session: QuerySessionResponse
): SessionWithOfflineMarker {
  return {
    ...session,
    [OFFLINE_LOCAL_SESSION_MARKER]: true,
  };
}

/**
 * True when a stored `template_id` is the Auto Mode sentinel rather than a real
 * template.
 *
 * One definition because two readers decode it — the cache mapper below and the
 * cold-boot stub builder — while the two WRITERS encode Auto differently on
 * purpose (create leaves the template unset; a mid-session switch writes the
 * sentinel). Keeping the decode in one place is what lets that asymmetry stay
 * deliberate: a drifting second copy would not fail loudly, it would surface as
 * Auto quietly reverting to the account-default template after a sync.
 */
export function isAutoModeTemplateId(templateId: string | null | undefined): boolean {
  return templateId === DEFAULT_TEMPLATE_AUTO_MODE;
}

/**
 * Maps a consult-note options patch onto the offline row's settings columns.
 *
 * Auto Mode is stored as the `AUTO_MODE` sentinel in `template_id`, not as a
 * column of its own. That is the same value the backend contract accepts for
 * `template_id`, and the sync engine already reads it as "generate with Auto",
 * so nothing downstream needs to learn a new field. It also has to be a
 * positive marker rather than a cleared template: a NULL `template_id` means
 * "the clinician chose nothing for this session", which both cold-boot
 * rehydration and the sync-time merge deliberately backfill from the account
 * default — which would silently restore the very template Auto replaced.
 */
export function toOfflineRecordingSettings(
  settings: OfflineConsultNoteSettings
): UpdateOfflineRecordingSettingsArgs {
  // Auto is the only generation method with an offline encoding, and this
  // narrowing is deliberate rather than incidental. The door type advertises
  // the whole enum, but the durable write is a partial update: the settings
  // statement coalesces an absent template onto the stored one, so any other
  // defined method would be dropped with nothing failing. That silent drop is
  // the failure class this mapper exists to close, so say it out loud instead
  // of letting a future caller rediscover it. An absent method is not a drop:
  // most callers patch only the note style or the scribe.
  const isAutoMode = settings.generationMethod === GenerationMethod.AUTO_MODE;

  if (settings.generationMethod !== undefined && !isAutoMode) {
    logger.warn(
      '[offline-recording] generation method has no offline encoding, not persisted',
      { generationMethod: settings.generationMethod }
    );
  }

  return {
    noteStyle: settings.noteStyle,
    templateId: isAutoMode ? DEFAULT_TEMPLATE_AUTO_MODE : undefined,
  };
}

export function applyOfflineSettingsToSessionCache(
  session: QuerySessionResponse | undefined,
  settings: UpdateOfflineRecordingSettingsArgs
): QuerySessionResponse | undefined {
  if (!session) {
    return undefined;
  }

  const next: QuerySessionResponse = { ...session };

  // Guard on value, not key presence. Callers build patches like
  // `{ language: variables.languageCode ?? undefined }`, so the key is always
  // present — a `'language' in settings` check would let an `undefined` value
  // wipe the cached field. Only apply settings that carry a defined value.
  if (settings.language !== undefined) {
    next.language_code = settings.language;
  }
  if (settings.outputLanguage !== undefined) {
    next.output_language_code = settings.outputLanguage;
  }
  // Only the Auto sentinel is decoded here. A real template id is deliberately
  // left alone: choosing a template is owned by the template writer, which does
  // its own optimistic cache write and is the only caller that knows whether the
  // template is an internal one. This mapper cannot tell the two apart, so
  // decoding a template here would flatten every internal template to a plain
  // one.
  const isAutoMode = isAutoModeTemplateId(settings.templateId);

  // One spread of the existing note: two independent `if` blocks each spreading
  // `session.consult_note` would drop whichever wrote first.
  if (settings.noteStyle !== undefined || isAutoMode) {
    next.consult_note = {
      ...session.consult_note,
      ...(settings.noteStyle !== undefined && { voice_style: settings.noteStyle }),
      ...(isAutoMode && {
        // The sentinel is a storage detail; the cache shows Auto as an absent
        // template so the picker renders "Auto" rather than a template named
        // after the sentinel.
        template_id: undefined,
        generation_method: GenerationMethod.AUTO_MODE,
      }),
    };
  }

  return next;
}
